import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, QrCode, Landmark, Copy, Check, MapPin } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { CityFilter } from '@/components/CityFilter'
import { spacing } from '@/theme'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

type Foundation = {
  id: string
  title: string
  description: string
  qrCode: string | null
  bankName1: string | null
  bankAccount1: string | null
  bankName2: string | null
  bankAccount2: string | null
  city: string | null
}

export function FundacionScreen() {
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  const [foundations, setFoundations] = useState<Foundation[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState<string | null>(null)
  const [cityFilter, setCityFilter] = useState('')

  useEffect(() => {
    api.get('/foundations/active').then((res) => {
      setFoundations(res.data.data || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const filteredFoundations = cityFilter ? foundations.filter((f) => f.city === cityFilter) : foundations

  const copyAccount = async (account: string) => {
    try {
      await navigator.clipboard.writeText(account)
      setCopied(account)
      toast.success('Numero de cuenta copiado')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('No se pudo copiar')
    }
  }

  const foundation = filteredFoundations[0]

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(180deg, #020617 0%, #0f172a 30%, #881337 70%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#fda4af',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 24,
              padding: spacing[5],
              marginBottom: spacing[4],
              background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.15) 0%, rgba(136, 19, 55, 0.4) 100%)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(244, 63, 94, 0.12) 0%, transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3], position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #f43f5e, #be123c)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(244, 63, 94, 0.3)',
              }}>
                <Heart size={24} color="#fff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#ffe4e6' }}>Fundación</h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#fda4af' }}>Tu ayuda transforma vidas, dona hoy</p>
              </div>
            </div>
          </motion.div>

          <div style={{ marginBottom: spacing[4] }}>
            <CityFilter value={cityFilter} onChange={setCityFilter} label="Filtrar por ciudad" />
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 20, padding: spacing[5], background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(244, 63, 94, 0.1)' }}>
                  <div style={{ width: 120, height: 120, borderRadius: 16, background: 'rgba(244, 63, 94, 0.1)', margin: '0 auto', marginBottom: spacing[3] }} />
                  <div style={{ width: '60%', height: 14, borderRadius: 8, background: 'rgba(244, 63, 94, 0.1)', margin: '0 auto' }} />
                </div>
              ))}
            </div>
          ) : !foundation ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <Heart size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>{cityFilter ? 'No hay fundación en esta ciudad' : 'Proximamente...'}</p>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  borderRadius: 20,
                  padding: spacing[5],
                  marginBottom: spacing[3],
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                  border: '1px solid rgba(244, 63, 94, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[2] }}>
                  <Heart size={18} color="#fda4af" />
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>{foundation.title}</h2>
                  {foundation.city && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#fda4af', marginLeft: 'auto' }}>
                      <MapPin size={13} /> {foundation.city}
                    </span>
                  )}
                </div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#cbd5e1' }}>
                  {foundation.description}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                style={{
                  borderRadius: 20,
                  padding: spacing[5],
                  marginBottom: spacing[3],
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                  border: '1px solid rgba(244, 63, 94, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[3] }}>
                  <QrCode size={18} color="#fda4af" />
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Escanea para donar</h3>
                </div>
                {foundation.qrCode ? (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{
                      padding: spacing[3],
                      background: '#ffffff',
                      borderRadius: 16,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}>
                      <img
                        src={foundation.qrCode}
                        alt="Código QR para donar"
                        style={{ width: 200, height: 200, objectFit: 'contain' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: 0, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>QR no disponible por ahora</p>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16 }}
                style={{
                  borderRadius: 20,
                  padding: spacing[5],
                  marginBottom: spacing[3],
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                  border: '1px solid rgba(244, 63, 94, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[3] }}>
                  <Landmark size={18} color="#fda4af" />
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Cuentas bancarias</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
                  {(foundation.bankName1 && foundation.bankAccount1) && (
                    <div style={{
                      borderRadius: 14,
                      padding: spacing[3],
                      background: 'rgba(244, 63, 94, 0.06)',
                      border: '1px solid rgba(244, 63, 94, 0.15)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
                        <Landmark size={15} color="#fda4af" />
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{foundation.bankName1}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing[2] }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#fda4af', letterSpacing: 1 }}>
                          {foundation.bankAccount1}
                        </span>
                        <button
                          onClick={() => copyAccount(foundation.bankAccount1!)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            borderRadius: 10,
                            border: '1px solid rgba(244, 63, 94, 0.3)',
                            background: 'rgba(244, 63, 94, 0.1)',
                            color: '#fda4af',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {copied === foundation.bankAccount1 ? <Check size={14} /> : <Copy size={14} />}
                          {copied === foundation.bankAccount1 ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    </div>
                  )}
                  {(foundation.bankName2 && foundation.bankAccount2) && (
                    <div style={{
                      borderRadius: 14,
                      padding: spacing[3],
                      background: 'rgba(244, 63, 94, 0.06)',
                      border: '1px solid rgba(244, 63, 94, 0.15)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2], marginBottom: spacing[1] }}>
                        <Landmark size={15} color="#fda4af" />
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{foundation.bankName2}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing[2] }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#fda4af', letterSpacing: 1 }}>
                          {foundation.bankAccount2}
                        </span>
                        <button
                          onClick={() => copyAccount(foundation.bankAccount2!)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            borderRadius: 10,
                            border: '1px solid rgba(244, 63, 94, 0.3)',
                            background: 'rgba(244, 63, 94, 0.1)',
                            color: '#fda4af',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {copied === foundation.bankAccount2 ? <Check size={14} /> : <Copy size={14} />}
                          {copied === foundation.bankAccount2 ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
